'use client'
import styles from './Rows.module.css'
import { useParams } from 'next/navigation'
import HeroSlider from '@/components/UI/HeroSlider/HeroSlider'
import VerticalRow from './VerticalRow/VerticalRow'
import HorizontalRow from './HorizontalRow/HorizontalRow'
import GameDropRow from './GameDropRow/GameDropRow'
import SpotlightRow from './SpotlightRow/SpotlightRow'
import { useSelf } from '@/apiUtilities/queries/useSelf'
import { useEffect, useRef } from 'react'
import { userHasActivePlan } from '@/utils/functions'
import { useModalStore } from '@/stores/useModalStore'
import FreeTrialActiveModal from '../Modals/FreeTrialActiveModal/FreeTrialActiveModal'
import { useUserStore } from '@/stores/useUserStore'
import { Row, RowContentType, RowStyle } from '@/interfaces/rows_types'
import { useRows } from '@/apiUtilities/queries/useRows'
import CategorySelector from './CategorySelector/CategorySelector'
import IconButton, { IconButtonKind, IconButtonSize } from '../UI/Buttons/IconButton/IconButton'
import IconUpArrow from '@/assets/img/Icon_ArrowUp.svg'
import { useTranslations } from 'next-intl'
import { toast } from 'react-toastify'
import Notification from "@/components/Notification/Notification"
import DiscoverBanner from './DiscoverBanner/DiscoverBanner'
import { FEATURES } from '@/utils/constants'

export default function Rows({ initialUserId, initialProfileId } : { initialUserId?: string, initialProfileId?: string }) {
	const params = useParams()
	const locale = params.locale as string
	const userId = useUserStore((s) => s.userId) ?? initialUserId
	const profileId = useUserStore((s) => s.profileId) ?? initialProfileId
	const self = useSelf({ enabled: !!userId })
	const { openModal, closeModal } = useModalStore()
	const { data, isLoading, isSuccess, error } = useRows({ locale, profileId })
	const t = useTranslations('Rows')
	const retryAttempted = useRef(false)
	const tc = useTranslations('Codes')


	useEffect(() => {
		// Reintento único después de 2 segundos si self.isSuccess pero falta self.data.purchase
		if (self.isSuccess && !self.data?.purchase && !retryAttempted.current) {
			retryAttempted.current = true
			const timeout = setTimeout(() => {
				self.refetch?.()
			}, 2000)
			return () => clearTimeout(timeout)
		}
	}, [self.isSuccess, self.data?.purchase])

	useEffect(() => {
		// Get query params from the URL
		const queryString = typeof window !== 'undefined' ? window.location.search : '';
		const params = new URLSearchParams(queryString);
		const showTrial = params.get('show_trial') === 'true';
		if(self.isSuccess && userHasActivePlan(self.data) && self.data.purchase.source === 'trial' && showTrial) {
			openModal({
				content: <FreeTrialActiveModal onClose={closeModal} expirationDate={self.data.purchase.expiration_time} />
			})
			// Remove show_trial param from URL
			params.delete('show_trial')
			const newUrl = params.toString() ? `?${params.toString()}` : window.location.pathname
			window.history.replaceState({}, '', newUrl)
		}
	}, [self.isSuccess, self.data?.purchase])

	useEffect(() => {
		const queryString = typeof window !== 'undefined' ? window.location.search : '';
		const params = new URLSearchParams(queryString);
		const success = params.get('success') === 'true';
		if(success) {
			toast.success(<Notification text={tc('promoCodeRedeemedSuccessfully')} />)
		
		}
	}, [])

	if (isLoading) return null
	if (error) return null

	const rows = data ? data.rows : undefined
	const heroSliderGames = data ? data.hero_slider : undefined
	const categorySelector = data ? data.category_selector : undefined
	if(!rows) return null

	return (
		<div className={styles.rows}>
			<div id={'top'}></div>
			{isSuccess && heroSliderGames && <HeroSlider games={heroSliderGames}/>}
			{isSuccess && rows &&
			<div className={styles.dynamicRows}>
				{(rows as Row[]).map((row, idx) => {
					switch(row.style) {
						case RowStyle.HorizontalCards:
							return (
								<HorizontalRow row={row} key={row.id} analyticsPosition={idx}  />
							)
						case RowStyle.VerticalCards:
							return (
								<VerticalRow row={row} key={row.id} analyticsPosition={idx} />
							)
						case RowStyle.Spotlight:
							return (
								<SpotlightRow row={row} key={row.id} analyticsPosition={idx}/>
							)
						case RowStyle.Inherited:
							switch(row.content_type) {
								case RowContentType.GameDrop:
									return (
										<GameDropRow row={row} key={row.id} />
									)
								default:
									return (
										<GameDropRow row={row} key={row.id} />
									)
							}
						default:
							return <HorizontalRow row={row} key={row.id} />
					}
				})}
			</div>}
			{isSuccess && !userId && FEATURES.discoverBanner && <DiscoverBanner />}
			{isSuccess && categorySelector && <CategorySelector />}
			{<div className={styles.backToTop}>
				<p>{t('backToTop')}</p>
			    <IconButton
					type="button"
					icon={IconUpArrow}
					title={'Back to top'}
					kind={IconButtonKind.FILLED}
					size={IconButtonSize.M}
					onClick={() => document.getElementById('top')?.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
					})}
					/>	
			</div>}
		</div>
	)


}